import axios from "axios";
import { BACKEND_SERVER_URL } from "constants/api.constant";
import { PERSIST_STORE_NAME } from "constants/app.constant";
import deepParseJson from "utils/deepParseJson";

let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (newAccessToken) => {
  refreshSubscribers.forEach((callback) => callback(newAccessToken));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

const api = axios.create({
  baseURL: `${BACKEND_SERVER_URL}`,
  withCredentials: true,
});

api.interceptors.request.use(
  async function (config) {
    if (config.url === "/auth/login") {
      config.headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
      };
    } else {
      const directToken = localStorage.getItem("auth_token");
      const rawPersistData = await localStorage.getItem(PERSIST_STORE_NAME);
      const persistData = deepParseJson(rawPersistData);
      const token = directToken || persistData?.auth?.session?.token;

      if (!config.headers["Content-Type"]) {
        config.headers["Content-Type"] = "application/json; charset=utf-8";
      }

      if (token && !config.headers.Authorization) {
        config.headers.Authorization = token.startsWith("Bearer ")
          ? token
          : `Bearer ${token}`;
      }
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  async function (response) {
    const isBlobResponse =
      response.config.responseType === "blob" ||
      response.config.responseType === "arraybuffer" ||
      response.headers?.["content-type"]?.includes("application/pdf") ||
      response.headers?.["content-type"]?.includes("application/octet-stream");

    if (isBlobResponse) {
      return response;
    }

    return response.data;
  },

  async function (error) {
    const originalRequest = error.config;

    const status = error.response?.status;
    const errorMessage = error.response?.data?.error?.message || "";
    const isTokenExpired =
      status === 401 &&
      (errorMessage === "JWT_EXPIRED" ||
        errorMessage.toLowerCase().includes("expired"));

    const isAuthEndpoint =
      originalRequest.url?.includes("/auth/refresh") ||
      originalRequest.url?.includes("/auth/login") ||
      originalRequest.url?.includes("/auth/logout");

    if (isTokenExpired && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      console.log("⚠️ [Axios] Access Token Expired. Initiating Refresh Flow...");

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          addRefreshSubscriber((newToken) => {
            originalRequest.headers.Authorization = newToken;
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        const refreshResponse = await axios.post(
          `${BACKEND_SERVER_URL}auth/refresh`,
          { refreshToken: localStorage.getItem("refresh_token") },
          { withCredentials: true },
        );

        const newAccessToken = refreshResponse.data?.accessToken;
        const newRefreshToken = refreshResponse.data?.refreshToken;

        if (!newAccessToken) {
          throw new Error("No access token in refresh response");
        }

        console.log("✅ [Axios] Successfully refreshed Access Token");

        const { default: store } = await import("store");
        const { updateToken } = await import("store/auth/sessionSlice");
        store.dispatch(updateToken(newAccessToken));
        localStorage.setItem("auth_token", newAccessToken);

        if (newRefreshToken) {
          console.log("✅ [Axios] Successfully rotated Refresh Token");
          localStorage.setItem("refresh_token", newRefreshToken);
        }

        onRefreshed(newAccessToken);
        isRefreshing = false;

        originalRequest.headers.Authorization = newAccessToken.startsWith(
          "Bearer ",
        )
          ? newAccessToken
          : `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        console.log("❌ [Axios] Refresh Failed. Logging out user.", refreshError?.message);
        isRefreshing = false;
        refreshSubscribers = [];

        const currentToken = localStorage.getItem("auth_token");
        if (
          currentToken &&
          currentToken !==
            originalRequest.headers?.Authorization?.replace("Bearer ", "")
        ) {
          originalRequest.headers.Authorization = currentToken.startsWith(
            "Bearer ",
          )
            ? currentToken
            : `Bearer ${currentToken}`;
          return api(originalRequest);
        }

        localStorage.removeItem("auth_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("admin");

        const { default: store } = await import("store");
        const { onSignOutSuccess } = await import("store/auth/sessionSlice");
        const { userLoggedOut } = await import("store/auth/userSlice");
        store.dispatch(onSignOutSuccess());
        store.dispatch(userLoggedOut());

        window.location.href = "/signin";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
